import React from 'react';
import ToggleFooterColumn from './FooterLinks/Toggle-FooterColumns';
import SocialMediaLinks from './SocialMediaLinks/SocialMediaLinks';
import translations from '../../text/get-started-translations.json';
import './footer.scss';
import anHKTLogoEn from '../../images/an-hkt-logo-w.svg';
import anHKTLogoZh from '../../images/footer-pccw-w.svg';

export interface FooterMenuItem {
    id: string | number;
    title: string;
    url: string;
    target?: string;
    children?: FooterMenuItem[];
}

export interface FooterColumn {
    title: string;
    links: FooterMenuItem[];
}

export interface FooterSocialLinks {
    facebook?: string;
    instagram?: string;
    youtube?: string;
}

export interface FooterProps {
    status?: 'normal' | 'copyrightOnly';
    bottomMenuLinks?: FooterMenuItem[];
    copyrightText?: string;
    dpmsText?: string;
    footerTagline?: string;
    language?: 'en' | 'zh';
}

const Footer: React.FC<FooterProps> = ({ 
    status = 'normal',
    bottomMenuLinks = [],
    copyrightText = '',
    dpmsText = '',
    footerTagline = '',
    language = 'en'
}) => {
    const pageTranslations = translations[language];
    const isStbMaxPage = typeof window !== 'undefined' && window.location.pathname === '/stb-hbomax';
    const anHKTLogo = language === 'zh' ? anHKTLogoZh : anHKTLogoEn;
    const hardcodedMenuColumns: FooterColumn[] = language === 'zh' ? [
        {
            title: '關於我們',
            links: [
                { id: 'about-1', title: 'Now TV', url: '/about/' },
                { id: 'about-2', title: '廣告商機', url: '/advertiser/' },
                { id: 'about-3', title: '反詐騙資訊', url: '/site/anti-scam-tips/' }
            ]
        },
        {
            title: '訂購',
            links: [
                { id: 'subscribe-1', title: '全新客戶', url: '/get-started/' },
                { id: 'subscribe-2', title: '現有客戶', url: '/subscriber-enquiry/' },
                { id: 'subscribe-3', title: '商業客戶', url: '/business/' },
                { id: 'subscribe-4', title: '頻道組合', url: '/available-channel-packs/' },
                { id: 'subscribe-5', title: '禮品換領', url: '/site/redemption/' }
            ]
        },
        {
            title: '節目推介',
            links: [
                { id: 'whatson-1', title: '頻道 & 自選服務', url: '/channel-overview/' }
            ]
        },
        {
            title: '觀看Now TV',
            links: [
                { id: 'watch-1', title: 'Now TV', url: 'https://nowplayer.now.com/?lang=zh' },
                { id: 'watch-2', title: '機頂盒用戶介面', url: '/tv-interface/' },
                { id: 'watch-3', title: '節目表', url: 'https://nowplayer.now.com/tvguide?lang=zh' },
                { id: 'watch-4', title: '機頂盒新用戶須知', url: '/site/user-guide/' }
            ]
        },
        {
            title: '賬戶',
            links: [
                { id: 'account-1', title: '我的賬戶', url: '/my-account/' },
                { id: 'account-2', title: '支援', url: '/support/' },
                { id: 'account-3', title: '終止服務', url: '/support/service-termination/' }
            ]
        },
        {
            title: '聯絡我們',
            links: [
                { id: 'contact-1', title: 'WhatsApp (客戶服務)', url: 'https://api.whatsapp.com/send?phone=85260551888' },
                { id: 'contact-2', title: 'WhatsApp (網上銷售團隊)', url: 'https://api.whatsapp.com/send?phone=85296133533' },
                { id: 'contact-3', title: '聯絡方式', url: '/contact-us/' }
            ]
        },
        {
            title: 'Now相關網站',
            links: [
                { id: 'more-1', title: 'Now.com', url: 'https://now.com/', target: '_blank' },
                { id: 'more-2', title: 'Now影視點播站', url: 'https://nowplayer.now.com/ondemand/seeall?lang=zh&?filterType=appPlayable&nodeId=201510160000066', target: '_blank' },
                { id: 'more-3', title: 'Now Sports', url: 'https://nowplayer.now.com/sports/schedule' },
                { id: 'more-4', title: 'Apps', url: '/interactive', target: '_blank' }
            ]
        }
    ] : [
        {
            title: 'About',
            links: [
                { id: 'about-1', title: 'Now TV', url: '/about/' },
                { id: 'about-2', title: 'Advertising', url: '/advertiser/' },
                { id: 'about-3', title: 'Anti-Scam', url: '/site/anti-scam-tips/' }
            ]
        },
        {
            title: 'Subscribe',
            links: [
                { id: 'subscribe-1', title: 'New Customers', url: '/get-started/' },
                { id: 'subscribe-2', title: 'Existing Customers', url: '/subscriber-enquiry/' },
                { id: 'subscribe-3', title: 'Business Customers', url: '/business/' },
                { id: 'subscribe-4', title: 'All Packs', url: '/available-channel-packs/' },
                { id: 'subscribe-5', title: 'Redeem Gifts', url: '/site/redemption/' }
            ]
        },
        {
            title: "What's On",
            links: [
                { id: 'whatson-1', title: 'Channels & On Demand', url: '/channel-overview/' }
            ]
        },
        {
            title: 'Watch Now TV',
            links: [
                { id: 'watch-1', title: 'Now TV', url: 'https://nowplayer.now.com/?lang=en' },
                { id: 'watch-2', title: 'Set Top Box TV Interface', url: '/tv-interface/' },
                { id: 'watch-3', title: 'TV Guide', url: 'https://nowplayer.now.com/tvguide?lang=en' },
                { id: 'watch-4', title: 'Set Top Box TV Tips for new users', url: '/site/user-guide/' }
            ]
        },
        {
            title: 'Account',
            links: [
                { id: 'account-1', title: 'My Account', url: '/my-account/' },
                { id: 'account-2', title: 'Support Centre', url: '/support/' },
                { id: 'account-3', title: 'Service Termination', url: '/support/service-termination/' }
            ]
        },
        {
            title: 'Contact Us',
            links: [
                { id: 'contact-1', title: 'WhatsApp (CS)', url: 'https://api.whatsapp.com/send?phone=85260551888' },
                { id: 'contact-2', title: 'WhatsApp (Sales)', url: 'https://api.whatsapp.com/send?phone=85296133533' },
                { id: 'contact-3', title: 'See All Contacts', url: '/contact-us/' }
            ]
        },
        {
            title: 'More Now',
            links: [
                { id: 'more-1', title: 'Now.com', url: 'https://now.com/', target: '_blank' },
                { id: 'more-2', title: 'Now Video Express', url: 'https://nowplayer.now.com/ondemand/seeall?lang=en&?filterType=appPlayable&nodeId=201510160000066', target: '_blank' },
                { id: 'more-3', title: 'Now Sports', url: 'https://nowplayer.now.com/sports/schedule' },
                { id: 'more-4', title: 'Apps', url: '/interactive', target: '_blank' }
            ]
        }
    ];

    const hardcodedSocialLinks: FooterSocialLinks = {
        facebook: 'https://www.facebook.com/NowTV.hongkong/',
        instagram: 'https://www.instagram.com/nowtv.hk/?hl=en',
        youtube: 'https://www.youtube.com/user/nowtv'
    };


    const hardcodedbottomMenuLinks = language === 'zh' ? [
        { title: '網頁指南', url: '/sitemap', target: '_self' },
        { title: '條款細則', url: '/terms-and-conditions', target: '_self' },
        { title: '免責聲明', url: '/disclaimer', target: '_self' },
        { title: '政策聲明', url: '/policy', target: '_self' },
        { title: 'Cookies 政策', url: '/wp-content/uploads/cookiespolicy/Cookies_Policy_ZH.pdf', target: '_self' }
    ]:[
        { title: 'Sitemap', url: '/sitemap', target: '_self' },
        { title: 'Terms & Conditions', url: '/terms-and-conditions', target: '_self' },
        { title: 'Disclaimer & Copyrights', url: '/disclaimer', target: '_self' },
        { title: 'Policies & Statements', url: '/policy', target: '_self' },
        { title: 'Cookies Policy', url: '/wp-content/uploads/cookiespolicy/Cookies_Policy_EN.pdf', target: '_self' }

    ];

    // Show footer only if there's content to display
    const hasContent = hardcodedMenuColumns.length > 0 || bottomMenuLinks.length > 0;

    if (!hasContent && status !== 'copyrightOnly') {
        return null;
    }

    return (
        <footer className="footer">
            {(status === 'normal' && hardcodedMenuColumns.length > 0 && !isStbMaxPage) && 
            <div className='footer-wrapper'>
                <div className='container'>
                    <div className='inner'>
                        {hardcodedMenuColumns.map((column, index) => (
                            <ToggleFooterColumn key={index} title={column.title}>
                                {column.links.map((link, linkIndex) => (
                                    <li key={linkIndex}>
                                        <a href={link.url} target={link.target || '_self'}>
                                            {link.title}
                                        </a>
                                    </li>
                                ))}
                            </ToggleFooterColumn>
                        ))}
                    </div>

                    <SocialMediaLinks 
                        facebook={hardcodedSocialLinks.facebook}
                        instagram={hardcodedSocialLinks.instagram}
                        youtube={hardcodedSocialLinks.youtube}
                    />
                </div>
            </div>
            }
             
            <div className="copyright-wrapper">
                <div className='container'>
                    <div className='inner'>
                        {!isStbMaxPage && (
                            bottomMenuLinks.length > 0 ? (
                                <div className="footer-links">
                                    {bottomMenuLinks.map((link, index) => (
                                        <a key={index} href={link.url} target={link.target || '_self'}>
                                            {link.title}
                                        </a>
                                    ))}
                                </div>
                            ): (
                                <div className="footer-links">
                                    {hardcodedbottomMenuLinks.map((link, index) => (
                                        <a key={index} href={link.url} target={link.target || '_self'}>
                                            {link.title}
                                        </a>
                                    ))}
                                </div>
                            )
                        )}
                        
                        {isStbMaxPage ? (
                            <div className="copyright-text" style={{ textAlign: 'center', width: '100%' }}>
                                <p>{pageTranslations.copyrightText}{new Date().getFullYear()}</p>
                            </div>
                        ) : (
                            <div className="copyright-text">
                                <p>{pageTranslations.copyrightText}{new Date().getFullYear()}  <span className="note-DPMS">{pageTranslations.dpmsText}</span>
                                </p>
                                <img src={anHKTLogo} alt="HKT Logo"/>
                                <small>{pageTranslations.dpmsText}</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
